import React from "react";
import { 
    Box, 
    Typography, 
    Button, 
    Container, 
    Grid, 
    Card, 
    CardContent,
    useTheme,
    Stack
} from "@mui/material";
import { tokens } from "../../theme";
import videoSrc from '../../images/video.mp4';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EngineeringIcon from '@mui/icons-material/Engineering';
import StarIcon from '@mui/icons-material/Star';
import SecurityIcon from '@mui/icons-material/Security';
import { Link } from "react-router-dom";

const Home = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const features = [
        {
            icon: <EngineeringIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: "Expert Craftsmanship",
            description: "Our skilled team brings years of experience and dedication to every project."
        },
        {
            icon: <StarIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: "Quality Materials",
            description: "We use only the highest quality materials to ensure lasting durability."
        },
        {
            icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
            title: "Guaranteed Work",
            description: "Your satisfaction is our priority, backed by our quality guarantee."
        }
    ];

    const services = [
        {
            title: "Curtain Wall",
            image: "https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=800",
            description: "Stick and unitised curtain walling for new-build and refurbishment projects, delivered with precision installation and quality assurance.",
            features: ["Stick & Unitised Systems", "New-Build & Refurbishment", "Precision Installation"]
        },
        {
            title: "Cladding",
            image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=800",
            description: "Façade remediation and replacement works, including cladding, glazing, and façade component replacement.",
            features: ["Cladding & Glazing Replacement", "Component Replacement", "Building Safety Compliance"]
        },
        {
            title: "Remediation",
            image: "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?auto=format&fit=crop&w=800",
            description: "Façade remediation and replacement works, with EPDM, insulation, and breather membrane installation.",
            features: ["EPDM & Insulation Installation", "Breather Membrane Installation", "Building Safety Compliance"]
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            {/* `isolation: isolate` makes this a stacking context, so the three z-indexes
                below are resolved against each other and cannot escape into the document.
                Without it the video's z-index would be measured against the page root,
                where anything painting a background over the canvas hides it. Keep the
                stack ordered 0/1/2 bottom-to-top rather than leaning on DOM order. */}
            <Box
                sx={{
                    position: "relative",
                    isolation: "isolate",
                    height: "90vh",
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                }}
            >
                {/* Background Video */}
                <video
                    src={videoSrc}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        zIndex: 0,
                    }}
                />

                {/* Overlay */}
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        zIndex: 1
                    }}
                />

                {/* Hero Content */}
                <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
                    <Stack spacing={4} alignItems="center" textAlign="center">
                        <Typography 
                            variant="h1" 
                            sx={{ 
                                color: "white",
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '4rem' },
                                fontWeight: 'bold',
                                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            Building Dreams Into Reality
                        </Typography>
                        <Typography 
                            variant="h4" 
                            sx={{ 
                                color: theme.palette.common.white,
                                maxWidth: '800px',
                                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.5rem' },
                                mb: 2,
                                px: { xs: 2, sm: 0 }
                            }}
                        >
                            Expert curtain wall, cladding, and remediation services delivered with precision and excellence.
                        </Typography>
                        <Stack 
                            direction={{ xs: 'column', sm: 'row' }} 
                            spacing={{ xs: 2, sm: 2 }}
                            sx={{ width: { xs: '100%', sm: 'auto' }, px: { xs: 3, sm: 0 } }}
                        >
                            <Button
                                component={Link}
                                to="/contact"
                                variant="contained"
                                size="large"
                                onClick={() => window.scrollTo(0, 0)}
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                    py: 2,
                                    px: 4,
                                    backgroundColor: theme.palette.primary.main,
                                    '&:hover': { backgroundColor: theme.palette.primary.dark },
                                    fontSize: { xs: '1rem', md: '1.1rem' },
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                Get Started
                            </Button>
                            <Button
                                component={Link}
                                to="/projects"
                                variant="outlined"
                                size="large"
                                sx={{
                                    py: 2,
                                    px: 4,
                                    borderColor: 'white',
                                    color: 'white',
                                    '&:hover': {
                                        borderColor: theme.palette.common.white,
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    },
                                    fontSize: { xs: '1rem', md: '1.1rem' },
                                    width: { xs: '100%', sm: 'auto' }
                                }}
                            >
                                View Our Work
                            </Button>
                        </Stack>
                    </Stack>
                </Container>
            </Box>

            {/* Features Section */}
            <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: colors.background[300] }}>
                <Container maxWidth="lg">
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {features.map((feature, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        backgroundColor: colors.background[200],
                                        transition: 'transform 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-8px)'
                                        }
                                    }}
                                >
                                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                        <Stack spacing={2} alignItems="center" textAlign="center">
                                            {feature.icon}
                                            <Typography 
                                                variant="h5" 
                                                sx={{ 
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 'bold',
                                                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                                                }}
                                            >
                                                {feature.title}
                                            </Typography>
                                            <Typography 
                                                variant="body1"
                                                sx={{ 
                                                    color: theme.palette.text.secondary,
                                                    fontSize: { xs: '1rem', md: '1.1rem' }
                                                }}
                                            >
                                                {feature.description}
                                            </Typography>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Services Section */}
            <Box sx={{ py: { xs: 6, md: 8 }, backgroundColor: colors.background[200] }}>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h2" 
                        sx={{ 
                            mb: { xs: 4, md: 6 },
                            textAlign: 'center',
                            color: theme.palette.text.primary,
                            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
                            px: { xs: 2, sm: 0 }
                        }}
                    >
                        Our Services
                    </Typography>
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {services.map((service, index) => (
                            <Grid item xs={12} md={4} key={index}>
                                <Card 
                                    sx={{ 
                                        height: '100%',
                                        backgroundColor: colors.background[300],
                                        transition: 'transform 0.3s ease-in-out',
                                        '&:hover': {
                                            transform: 'translateY(-8px)'
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: { xs: 250, md: 200 },
                                            overflow: 'hidden'
                                        }}
                                    >
                                        <img
                                            src={service.image}
                                            alt={service.title}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    </Box>
                                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                                        <Typography 
                                            variant="h5" 
                                            gutterBottom
                                            sx={{ 
                                                color: theme.palette.text.primary,
                                                fontWeight: 'bold',
                                                fontSize: { xs: '1.25rem', md: '1.5rem' }
                                            }}
                                        >
                                            {service.title}
                                        </Typography>
                                        <Typography 
                                            variant="body1"
                                            sx={{ 
                                                color: theme.palette.text.secondary,
                                                fontSize: { xs: '1rem', md: '1.1rem' }
                                            }}
                                        >
                                            {service.description}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* Call to Action */}
            <Box 
                sx={{ 
                    py: { xs: 6, md: 8 },
                    px: { xs: 2, sm: 0 },
                    backgroundColor: theme.palette.primary.dark,
                    color: theme.palette.common.white
                }}
            >
                <Container maxWidth="lg">
                    <Stack spacing={4} alignItems="center" textAlign="center">
                        <Typography 
                            variant="h3"
                            sx={{ 
                                fontWeight: 'bold',
                                color: 'inherit',
                                fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }
                            }}
                        >
                            Ready to Start Your Project?
                        </Typography>
                        <Typography 
                            variant="h6"
                            sx={{ 
                                maxWidth: '600px',
                                color: 'rgba(255, 255, 255, 0.9)',
                                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' }
                            }}
                        >
                            Contact us today for a free consultation and estimate.
                        </Typography>
                        <Button
                            component={Link}
                            to="/contact"
                            variant="contained"
                            size="large"
                            onClick={() => window.scrollTo(0, 0)}
                            sx={{
                                py: 2,
                                px: 4,
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                },
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                width: { xs: '100%', sm: 'auto' }
                            }}
                        >
                            Contact Us
                        </Button>
                    </Stack>
                </Container>
            </Box>
        </Box>
    );
};

export default Home;
